import * as s from "./AdminModalStyled";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import { useCallback, useState } from "react";
import { useAppSelector } from "redux/hooks";
import useStatusMutation from "hook/useStatusMutation";
import { notify } from "components/atoms/notification/Notification";
import { checkEmailRegEx } from "utils/regEx";
import { ValidateWord } from "components/atoms/text/Text";
import { postInviteOwner } from "api/userAPI";
import NormalConfirmModal from "components/templates/modal/small/confirm/normal";
import useApiError from "hook/useApiError";
import AccountInfoLabel from "components/atoms/text/labels/AccountInfoLabel";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { PiWarningCircleBold } from "react-icons/pi";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  onModal: () => void;
}

export default function InvitationAdminModal(props: Props) {
  const [email, setEmail] = useState<string>("");
  const selectedAccount = useAppSelector((state) => state.accountSettings);
  const [validateMsg, setValidateMsg] = useState<string>("");
  const { intlString, lang, handleErrorNotify } = useApiError();
  const mutationInviteOwner = useStatusMutation(postInviteOwner, {
    onSuccess: (res) => {
      if (res.error || res.errorString) {
        notify(
          "error",
          res.errorString
            ? res.errorString
            : intlString(lang.validateMsg.account.invite.fail)
        );
      } else {
        notify("success", intlString(lang.validateMsg.account.invite.success));
        props.onModal();
      }
    },
    onError: (err) => {
      handleErrorNotify(err, lang.validateMsg.account.invite.fail);
    },
  });
  const onClickInvite = useCallback(() => {
    if (!mutationInviteOwner.idle) return;
    setValidateMsg("");
    if (checkEmpty(email)) {
      setValidateMsg(intlString(lang.validateMsg.profile.email.empty));
      return;
    }
    if (!checkEmailRegEx(email)) {
      setValidateMsg(intlString(lang.validateMsg.profile.email.irregular));
      return;
    }
    mutationInviteOwner.mutate({
      accountId: selectedAccount.accountId,
      email,
    });
  }, [email, selectedAccount.accountId]);
  return (
    <NormalConfirmModal
      onConfirmCancel={() => {
        props.onModal();
      }}
      onConfirm={() => {
        onClickInvite();
      }}
      confirmLabel="Invite"
      cancelLabel="Cancel"
    >
      <s.AccessNotifyWrapper>
        <s.AccessNotifyTitle>
          <PiWarningCircleBold size={24} /> Administrator Required
        </s.AccessNotifyTitle>
        <AccountInfoLabel
          level={selectedAccount.accountLevel}
          accountNumber={selectedAccount.accountNumber}
          accountName={selectedAccount.accountName}
        />
        <p>
          End-User Organization must have at least one Administrator. <br />
          To access and manage <b>"{selectedAccount.accountName}"</b>, please
          invite the primary user (Owner) by entering their email address below.
        </p>
        <ModalInput label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(toTrim(e.target.value))}
            data-testid="users-admin-admin-input-set-email"
          />

          {validateMsg && (
            <ValidateWord data-testid="users-admin-validate-email">
              {validateMsg}
            </ValidateWord>
          )}
        </ModalInput>
      </s.AccessNotifyWrapper>
    </NormalConfirmModal>
  );
}
